import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {Icon, Link, Typography} from 'bonprix-design-system';
import {Constants} from '../view-constants';
import Landmark from '@core/scripts/components/landmark-adapter';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {removeSingleFilterFromParams, yieldToMain} from '../ArticleOverviewHelper';
import {Alert} from 'bonprix-design-system';
import { useSelector} from 'react-redux';
import {selectContentAjaxCall} from '../AjaxCallSlice.jsx';
import {useChangeFilters} from '../hooks/use-change-filters.js';
import {useBreakpointDown, useBreakpointUp} from 'bonprix-design-system';

interface FilterToken {
    filterIdentifier: string;
    filterOptionIdentifier: string;
    filterOptionName: string;
    filterName: string;
    isShowFilterName: boolean;
    trackingId: string;
    isRange: boolean;
}

interface FilterTokensProps {
    filterTokens: FilterToken[];
}


const APPROX_CONTAINER_HEIGHT_MOBILE_THREE_ROWS = 114

export default function FilterTokens({filterTokens}: FilterTokensProps) {
    const {texts, shopId, currentSearchParams} = useContext(articleOverviewStaticContext);
    const noFilterHitsInChosenCategory = useSelector(selectContentAjaxCall).retainShopIdFilterOnly;
    const {resetFilters, setFilters} = useChangeFilters();

    const isMobile = useBreakpointDown('m');
    const isLargeUp = useBreakpointUp('l');
    const isXLargeUp = useBreakpointUp('xl');
    const [showAllFilters, setShowAllFilters] = useState(false);
    const [needsFadeEffect, setNeedsFadeEffect] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const removeToken = useCallback(
        (identifier: string, value: string, isRange: boolean) => {
            const params = removeSingleFilterFromParams(identifier, value, isRange, currentSearchParams.current);
            setFilters(params);
        },
        [setFilters],
    );

    const removeAllTokens = useCallback(() => {
        yieldToMain(() => {
            resetFilters();
            sendFilterDeselectionLandmark(
                Constants.LANDMARK_FILTER_DESELECTION_ALL,
                Constants.LANDMARK_FILTER_DESELECTION_ALL,
            );
        });
    }, [resetFilters]);

    const removeSingleToken = useCallback(
        (identifier: string, value: string, trackingId: string, isRange: boolean) => {
            yieldToMain(() => {
                removeToken(identifier, value, isRange);
                sendFilterDeselectionLandmark(trackingId, Constants.LANDMARK_FILTER_DESELECTION_SINGLE);
            });
        },
        [removeToken],
    );

    const handleShowAllFilters = useCallback(() => {
        setShowAllFilters(true);
    }, []);

    useEffect(() => {
        if (!isMobile || showAllFilters) {
            setNeedsFadeEffect(false);
            return;
        }

        const checkHeight = () => {
            if (containerRef.current) {
                const containerHeight = containerRef.current.scrollHeight;
                setNeedsFadeEffect(containerHeight > APPROX_CONTAINER_HEIGHT_MOBILE_THREE_ROWS);
            }
        };

        checkHeight();
        window.addEventListener('resize', checkHeight);
        return () => window.removeEventListener('resize', checkHeight);
    }, [isMobile, showAllFilters, filterTokens.length]);

    function sendFilterDeselectionLandmark(value: string, type: string) {
        const shopIdFormatted = shopId.current ? `${shopId.current}` : '0';

        let landmarkData = {
            filter_click: `${Constants.LANDMARK_FILTER_DESELECTION}|${value}|${type}|${Constants.LANDMARK_FILTER_POSITION}`,
            filter_click_shopId: `${shopIdFormatted}`,
            fixation: Constants.LANDMARK_NON_STICKY,
        };

        Landmark.sendLandmark({
            landmark: 'userinterface',
            ...landmarkData,
        });
    }

    function renderToken(token: FilterToken, index: number) {
        const handleClick = () => {
            removeSingleToken(token.filterIdentifier, token.filterOptionIdentifier, token.trackingId, token.isRange);
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
            }
        };

        const filterName = `${token.filterOptionName}${token.isShowFilterName ? ` (${token.filterName})` : ''}`;
        const tokenHeight = isMobile ? 'h-[30px]' : 'h-[40px]';

        return (
            <div
                data-testid={`filter-token-${token.filterIdentifier}-${token.filterOptionIdentifier}`}
                key={index}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                role="button"
                className={`${tokenHeight} inline-flex items-center gap-2 px-2 py-[9px] bg-white border border-solid border-clr-on-primary-mid rounded hover:bg-clr-on-primary-lowest focus:outline-none focus:ring-2 focus:ring-clr-primary transition-colors cursor-pointer`}
                test-ref__filter-token={token.filterName}
                aria-label={`${texts.filter_reset} ${filterName}`}
            >
                <Typography variant="h5">
                    {filterName}
                </Typography>
                <Icon variant="closer" className="text-black w-4 h-4 flex-shrink-0" />
            </div>
        );
    }

    if (filterTokens.length === 0) {
        return (
            <>
                {noFilterHitsInChosenCategory && (
                    <div className="flex items-center px-2.5 py-2 xl:px-11">
                        <Alert
                            isOpen={true}
                            status={'info'}
                            variant={'secondary'}
                            showCloseIcon={false}
                            title={texts.filter_removedByUnavailability}
                        >
                        </Alert>
                    </div>
                )}
            </>
        );
    }

    const wrapperClasses = needsFadeEffect
        ? 'relative overflow-hidden'
        : '';
    const wrapperStyle = needsFadeEffect
        ? { maxHeight: `${APPROX_CONTAINER_HEIGHT_MOBILE_THREE_ROWS}px` }
        : undefined;
    const fadeStyle = needsFadeEffect
        ? {
              position: 'absolute' as const,
              bottom: 0,
              left: 0,
              right: 0,
              height: '64px',
              background: 'linear-gradient(to top, rgba(255,255,255,1), rgba(255,255,255,0))',
              pointerEvents: 'none' as const,
          }
        : undefined;

    const paddingX = isXLargeUp ? 'px-10' : 'px-2';
    const paddingY = isLargeUp ? 'py-5' : 'py-3';

    return (
        <div className={`${paddingX} ${paddingY} mt-1 ml-1 `}>
            <div className={wrapperClasses} style={wrapperStyle}>
                <div ref={containerRef} className="flex flex-wrap gap-2.5">
                    {filterTokens.map((token, index) => renderToken(token, index))}

                    <div className={'self-center ' + (isXLargeUp ? 'pl-4' : '')}>


                    <Link
                        component="button"
                        small={false}
                        onClick={removeAllTokens}
                    >
                        {texts.filter_reset}
                    </Link>
                    </div>
                </div>
                {needsFadeEffect && <div style={fadeStyle} />}
            </div>

            {isMobile && needsFadeEffect && !showAllFilters && (
                <div className="mt-2">
                    <Link
                        component="button"
                        onClick={handleShowAllFilters}
                    >
                        {texts.filter_show_all}
                    </Link>
                </div>
            )}
        </div>
    );
}
